import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Shield, Coins, Users, ArrowRight, HandHeart, Sparkles, X, Gift, Lock, KeyRound } from "lucide-react";
import confetti from "canvas-confetti";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HERO, LOGO } from "../lib/constants";
import { Button } from "../components/ui/button";
import ProfileCard from "../components/ProfileCard";
import JarWidget from "../components/JarWidget";
import TipModal from "../components/TipModal";
import HeroVideo from "../components/HeroVideo";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  const [impact, setImpact] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [tipTarget, setTipTarget] = useState(null);
  const [jarOpen, setJarOpen] = useState(false);

  useEffect(() => {
    api.get("/profiles?page=1&per_page=8").then((r) => setFeatured(r.data.profiles)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/impact").then((r) => { setImpact(r.data); checkMilestone(r.data.total_given); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MILESTONES = [1000, 2500, 5000, 10000, 25000, 50000, 100000];
  const checkMilestone = (total) => {
    const reached = MILESTONES.filter((m) => total >= m);
    if (reached.length === 0) return;
    const highest = reached[reached.length - 1];
    let seen = 0;
    try { seen = parseInt(localStorage.getItem("haplap_milestone_seen") || "0", 10) || 0; } catch { /* ignore */ }
    if (highest > seen) {
      setMilestone(highest);
      try { localStorage.setItem("haplap_milestone_seen", String(highest)); } catch { /* ignore */ }
      setTimeout(() => {
        const burst = (x) => confetti({ particleCount: 70, spread: 70, origin: { x, y: 0.25 }, colors: ["#D96A8F", "#E6A62E", "#7FBF97", "#F2A98B"] });
        burst(0.3); burst(0.7);
        setTimeout(() => burst(0.5), 250);
      }, 400);
    }
  };

  return (
    <div>
      {milestone && (
        <div data-testid="milestone-banner"
          className="sticky top-14 sm:top-16 z-40 bg-gradient-to-r from-copper via-penny to-sage text-white px-4 py-3 flex items-center justify-center gap-3 shadow-md animate-in slide-in-from-top duration-300">
          <p className="text-sm sm:text-base font-medium text-center">
            🎉 Together we've shared over <b>${milestone.toLocaleString()}</b> — that's {(milestone * 100).toLocaleString()} pennies passed along! 💛
          </p>
          <button data-testid="milestone-close" onClick={() => setMilestone(null)}
            className="p-1 rounded-full hover:bg-white/20 shrink-0" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero explainer video — the first thing visitors see */}
      <HeroVideo />

      {/* Hero */}
      <section data-testid="hero-section" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-14 pb-10 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-2 bg-penny/15 text-bronze px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest">
          <Coins className="w-3.5 h-3.5" /> Have a penny · Leave a penny
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-bronze leading-[1.05] mt-4">
          Ask for help.<br />Give a little.<br />No judgment.
        </h1>
        <p className="text-base sm:text-lg text-foreground/70 mt-5 max-w-xl leading-relaxed">
          HAPLAP is the countertop penny dish, reimagined for everyone. A warm, safe place where people share
          their story and the community answers with kindness — one penny at a time.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-7">
          <Button data-testid="hero-explore-btn" onClick={() => navigate("/discover")}
            className="bg-copper hover:bg-copper/90 text-white rounded-full py-6 px-7 text-base">
            Explore profiles <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button data-testid="hero-join-btn" onClick={() => navigate(user ? "/feed" : "/register")} variant="outline"
            className="rounded-full py-6 px-7 text-base border-copper/40 text-copper hover:bg-copper/10">
            {user ? "Go to timeline" : "Ask for help"}
          </Button>
        </div>
        {stats && (
          <div className="flex justify-center gap-8 sm:gap-12 mt-9">
            <div><p className="font-serif text-3xl font-bold text-bronze">{stats.members}</p><p className="text-xs text-muted-foreground">members</p></div>
            <div><p className="font-serif text-3xl font-bold text-bronze">{stats.seekers}</p><p className="text-xs text-muted-foreground">seeking help</p></div>
            <div><p className="font-serif text-3xl font-bold text-bronze">${stats.jar_distributed.toFixed(0)}</p><p className="text-xs text-muted-foreground">jar shared</p></div>
          </div>
        )}
      </section>

      {/* Jar */}
      <section className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col items-center text-center">
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-bronze mb-1">The Community Tip Jar</h2>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
        <p className="text-foreground/70 mt-1 mb-6 leading-relaxed max-w-lg">
          Don't want to pick just one person? Drop a little in the shared jar. Every single day it's divided
          <span className="font-semibold text-bronze"> equally</span> among everyone seeking support — a real boost of hope for every member.
        </p>
        <div className="w-full"><JarWidget onContribute={() => setJarOpen(true)} /></div>
        <div className="flex items-center justify-center gap-3 mt-5 text-sm text-muted-foreground">
          <HandHeart className="w-5 h-5 text-sage" /> 100% goes to the community. Give what you can.
        </div>
      </section>

      {/* Impact counter — live community totals */}
      <section data-testid="impact-section" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold tracking-widest uppercase bg-copper/12 text-copper px-3.5 py-1 rounded-full border border-copper/20">
          <Coins className="w-3.5 h-3.5" /> Our Impact So Far
        </span>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-bronze mt-3">Every penny adds up</h2>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
        <p className="text-foreground/70 max-w-lg leading-relaxed">
          Real kindness, shared in real time by this community — one penny at a time.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full max-w-2xl mx-auto mt-7">
          <div data-testid="impact-total-given" className="rounded-3xl bg-white border border-border/80 p-7 shadow-sm flex flex-col items-center gap-1.5">
            <div className="w-12 h-12 rounded-full bg-penny/15 text-penny flex items-center justify-center mb-1"><HandHeart className="w-6 h-6" /></div>
            <p className="font-serif text-4xl sm:text-5xl font-bold text-copper tracking-tight">
              ${impact ? impact.total_given.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
            </p>
            <p className="text-sm font-medium text-bronze">Total given to neighbours</p>
            {impact && (
              <p className="text-xs text-muted-foreground font-mono">
                that's {impact.pennies_shared.toLocaleString()} pennies shared 🪙
              </p>
            )}
          </div>
          <div data-testid="impact-gifts-sent" className="rounded-3xl bg-white border border-border/80 p-7 shadow-sm flex flex-col items-center gap-1.5">
            <div className="w-12 h-12 rounded-full bg-sage/15 text-sage flex items-center justify-center mb-1"><Sparkles className="w-6 h-6" /></div>
            <p className="font-serif text-4xl sm:text-5xl font-bold text-copper tracking-tight">
              {impact ? impact.gifts_sent.toLocaleString() : "—"}
            </p>
            <p className="text-sm font-medium text-bronze">Gifts sent in the community</p>
            <p className="text-xs text-muted-foreground">little tokens of encouragement 💛</p>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col items-center">
        <div className="flex flex-col items-center text-center mb-6">
          <p className="font-mono text-xs uppercase tracking-widest text-copper">Discovery</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-bronze mt-1">People to meet today</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full">
          {featured.map((p) => <ProfileCard key={p.user_id} profile={p} onTip={setTipTarget} />)}
        </div>
        <Link to="/discover" className="mt-8"><Button className="bg-copper hover:bg-copper/90 text-white rounded-full py-6 px-7">See everyone <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </section>

      {/* How + safety */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-10">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Build your profile", body: "Share your story, photos, and what you need. Fully yours to edit anytime." },
            { icon: Heart, title: "Receive kindness", body: "Get direct tips to your wallet, plus your share of the daily community jar." },
            { icon: Shield, title: "Stay safe", body: "Zero tolerance for harassment. Report anyone, anytime. Bad actors are removed instantly." },
          ].map((c) => (
            <div key={c.title} className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center">
              <div className="w-11 h-11 rounded-xl bg-copper/15 flex items-center justify-center text-copper"><c.icon className="w-5 h-5" /></div>
              <h3 className="font-serif text-xl font-semibold text-bronze mt-4">{c.title}</h3>
              <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Neighbours in Need — what it is & how to unlock it */}
      <section data-testid="neighbors-explainer" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold tracking-widest uppercase bg-sage/15 text-sage px-3.5 py-1 rounded-full border border-sage/25">
          <HandHeart className="w-3.5 h-3.5" /> Neighbours in Need
        </span>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-bronze mt-3">Help a neighbour — together</h2>
        <div className="w-20 h-1 bg-gradient-to-r from-sage via-penny to-copper rounded-full my-2.5 opacity-90" />
        <p className="text-foreground/75 max-w-2xl leading-relaxed">
          Sometimes one person's kindness isn't enough on its own. <b className="text-bronze">Neighbours in Need</b> lets a small,
          private circle pool their money — and even gift cards — into one shared fund that goes directly to a single family or
          neighbour who needs a hand. Think of it as the whole street quietly chipping in for one person, with dignity and zero judgment.
        </p>

        <div className="mt-6 inline-flex items-start gap-3 bg-penny/10 border border-penny/30 rounded-2xl px-5 py-4 max-w-2xl text-left">
          <Lock className="w-5 h-5 text-penny shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/85">
            <b className="text-bronze">Reserved for active supporters.</b> To keep this feature in the hands of people who already give
            back, you can <b>start a Neighbours group once you've personally supported 5 different neighbours</b> with a tip. Every tip you
            send brings you one step closer to unlocking it. 💛
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 w-full max-w-4xl mt-8 text-left">
          {[
            { icon: Heart, step: "1", title: "Give first to unlock", body: "Support 5 different neighbours with a tip. That's how you earn access — this feature is for people already spreading kindness." },
            { icon: KeyRound, step: "2", title: "Create or join a circle", body: "Start a private group and share its join code with people you trust, or join a group a neighbour invites you to." },
            { icon: Gift, step: "3", title: "Pool your kindness", body: "Everyone adds money (and gift cards) to one shared fund that goes straight to the chosen family or neighbour in need." },
          ].map((c) => (
            <div key={c.step} className="bg-card rounded-2xl border border-border p-6 flex flex-col items-start">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full bg-sage/15 text-sage font-bold text-sm flex items-center justify-center">{c.step}</span>
                <c.icon className="w-5 h-5 text-copper" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-bronze">{c.title}</h3>
              <p className="text-sm text-foreground/70 mt-1.5 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <Link to="/neighbors" className="mt-8" data-testid="neighbors-explainer-cta">
          <Button className="bg-sage hover:bg-sage/90 text-white rounded-full py-6 px-7">Explore Neighbours in Need <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </Link>
      </section>

      <TipModal open={!!tipTarget} onClose={() => setTipTarget(null)} mode="individual" recipient={tipTarget} />
      <TipModal open={jarOpen} onClose={() => setJarOpen(false)} mode="community" />
    </div>
  );
}
